import * as React from "react";
import { useState, useEffect, useMemo, useCallback } from "react";
import {
  DictationRefs,
  DictationSetters,
  UseDictationOptions,
  WebSocketHandlerParams,
  cleanupAudioRefs,
  executeStartRecording,
  executeStopRecording,
  resetAllStates,
} from "./dictation.utils";
import { useStopDictationIfInactive } from "./dictation-inactive.utils";
import { useUser } from "./useUser";
import { useDocument } from "./useDocument";

function useDictationRefs(): DictationRefs {
  // useMemo with an empty dependency array ensures the same plain-object wrapper is
  // returned on every render.  Each useRef() call still returns its stable ref object;
  // wrapping them in a memoised container means closures (cleanupAudio, wsHandlerParams)
  // always reference the same DictationRefs object and never become stale.
  return useMemo<DictationRefs>(
    () => ({
      socketRef: { current: null } as React.MutableRefObject<WebSocket | null>,
      mediaRecorderRef: { current: null } as React.MutableRefObject<MediaRecorder | null>,
      audioStreamRef: { current: null } as React.MutableRefObject<MediaStream | null>,
      audioContextRef: { current: null } as React.MutableRefObject<AudioContext | null>,
      analyserRef: { current: null } as React.MutableRefObject<AnalyserNode | null>,
      audioProcessorRef: { current: null } as React.MutableRefObject<ScriptProcessorNode | null>,
      silenceTimerRef: { current: null } as React.MutableRefObject<ReturnType<
        typeof setTimeout
      > | null>,
      checkRecorderDataRef: { current: null } as React.MutableRefObject<ReturnType<
        typeof setInterval
      > | null>,
      isRecordingRef: { current: false } as React.MutableRefObject<boolean>,
      isMicroLoadingRef: { current: false } as React.MutableRefObject<boolean>,
      isSpeakingRef: { current: false } as React.MutableRefObject<boolean>,
    }),
    []
  );
}

function useSyncRefsToState(
  refs: DictationRefs,
  isSpeaking: boolean,
  isRecording: boolean,
  isMicroLoading: boolean
) {
  useEffect(() => {
    refs.isSpeakingRef.current = isSpeaking;
  }, [refs, isSpeaking]);
  useEffect(() => {
    refs.isRecordingRef.current = isRecording;
  }, [refs, isRecording]);
  useEffect(() => {
    refs.isMicroLoadingRef.current = isMicroLoading;
  }, [refs, isMicroLoading]);
}

function useDictationState() {
  const [isRecording, setIsRecording] = useState(false);
  const [isMicroLoading, setIsMicroLoading] = useState(false);
  const [isConverting, setIsConverting] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [isSpeaking, setIsSpeaking] = useState(false);
  const refs = useDictationRefs();
  const setters = useMemo<DictationSetters>(
    () => ({
      setIsRecording,
      setIsMicroLoading,
      setIsConverting,
      setTranscript,
      setIsSpeaking,
    }),
    []
  );
  useSyncRefsToState(refs, isSpeaking, isRecording, isMicroLoading);
  return {
    isRecording,
    isMicroLoading,
    isConverting,
    transcript,
    isSpeaking,
    refs,
    setters,
    setTranscript,
  };
}

export const useDictation = (options: UseDictationOptions = {}) => {
  const { usePCMFormat = true, isActive = true } = options;
  const state = useDictationState();
  const { refs, setters } = state;
  const { user } = useUser();
  const { data: doc } = useDocument();

  const cleanupAudio = useCallback(() => cleanupAudioRefs(refs), [refs]);
  useEffect(() => () => cleanupAudio(), [cleanupAudio]);

  const stopRecording = useCallback(
    () => executeStopRecording(usePCMFormat, refs, setters, cleanupAudio),
    [usePCMFormat, refs, setters, cleanupAudio]
  );
  useStopDictationIfInactive(isActive, state.isRecording, state.isMicroLoading, stopRecording);

  const wsHandlerParams: WebSocketHandlerParams = {
    options,
    usePCMFormat,
    refs,
    setters,
    user,
    doc,
    cleanupAudio,
  };

  const startRecording = () => {
    // executeStartRecording is async — catch here so a rejected promise never
    // becomes an unhandled rejection that leaves isMicroLoading stuck true.
    executeStartRecording(
      options,
      usePCMFormat,
      refs,
      setters,
      wsHandlerParams,
      cleanupAudio
    ).catch((err: unknown) => {
      console.error("[DictationDebug] Unhandled startRecording error:", err);
      cleanupAudio();
      resetAllStates(refs, setters);
    });
  };
  const startDictation = () => {
    if (!state.isRecording && !state.isMicroLoading) {
      startRecording();
    } else {
      stopRecording();
    }
  };

  return {
    isRecording: state.isRecording,
    isMicroLoading: state.isMicroLoading,
    isConverting: state.isConverting,
    transcript: state.transcript,
    isSpeaking: state.isSpeaking,
    startDictation,
    stopRecording,
    setTranscript: state.setTranscript,
  };
};
